import React, {useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Dropdown, DropDownItem} from "../common/DropDown";
import {
    BookmakerData,
    BookmakerObject,
    EventsStoredObjects,
    MonitorSettingsObject,
    User,
} from "../../../@types/response";
import {useDispatch, useSelector} from "react-redux";
import {getBookmakers} from "../../../store/selectors/getBookmakers";
import FilterInput from "../common/FilterInput";
import {getErrorMessage} from "../../../store/selectors/getErrorMessage";
import {Autocomplete} from "../common/Autocomplete";
import {getActiveEventIds} from "../../../store/selectors/monitor/getActiveEventIds";
import {getFixtures} from "../../../store/selectors/getFixtures";
import {ActiveEventIds, searchActiveFixtures} from "../../../store/utils/searchFixtures";
import {faTrophy} from "@fortawesome/free-solid-svg-icons/faTrophy";
import {Drawer} from "../common/Drawer";
import TemplateButton from "./_template";
import {getTemplate} from "../../../store/selectors/monitor/getTemplate";
import {getTemplates} from "../../../store/selectors/monitor/getTemplates";
import {faRetweet} from "@fortawesome/free-solid-svg-icons";
import Logo from "../logo";
import Separator from "../Separator";
import {SetSettingsAction} from "../../../store/actions/monitor/SetSettingsAction";
import {GetEventsAction} from "../../../store/actions/monitor/GetEventsAction";
import {getSettings} from "../../../store/selectors/monitor/getSettings";
import {isInitialized} from "../../../store/selectors/monitor/isInitialized";
import {HeartBeatDropDown} from "../common/HeartBeatDropDown";
import {getComparisons} from "../../../store/selectors/getComparisons";
import {ComparisonsDropdown} from "./DropDown";
import getClient from "../../../store/selectors/getClient";
import {AppDispatch} from "recharts/types/state/store";

export default function Toolbar() {
    const allBookmakers: BookmakerData = useSelector(getBookmakers);
    const comparisons: BookmakerObject[] = useSelector(getComparisons);

    const template: null | string = useSelector(getTemplate);
    const templates: string[] = useSelector(getTemplates);
    const fixtures: EventsStoredObjects = useSelector(getFixtures);
    const activeEventIds: ActiveEventIds = useSelector(getActiveEventIds);
    const errorMessage: string = useSelector(getErrorMessage);
    const storedSettings: MonitorSettingsObject = useSelector(getSettings);
    const loading: boolean = useSelector(isInitialized);
    const client: User | undefined = useSelector(getClient);

    const [pendingSettings, setPendingSettings] = useState<boolean>(false);
    const [showCompetition, setShowCompetition] = useState<boolean>(false);
    const [settings, setSetting] = useState<MonitorSettingsObject>(storedSettings);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const suggestions = useMemo(
            () => searchActiveFixtures(fixtures, activeEventIds, searchQuery),
            [fixtures, activeEventIds, searchQuery]
    );

    const dispatch = useDispatch<AppDispatch>();

    const bookmakers: BookmakerData = useMemo(() => {
        return Object.keys(allBookmakers)
                .filter(id => client?.client == null || client.client === parseInt(id))
                .reduce((a, v) => ({...a, [v]: allBookmakers[v]}), {})
    }, [allBookmakers])

    useEffect(() => {
        setSetting(storedSettings);
    }, [storedSettings]);

    useEffect(() => {
        setPendingSettings(JSON.stringify(settings) !== JSON.stringify(storedSettings));
    }, [settings, storedSettings]);

    useEffect(() => {
        document.title = `${template ? template + ' - ' : ''}MONITOR - OddMonitor`;
    }, [template]);

    return (
            <div className="flex w-full h-14 2xl:text-monitorBase text-monitorSm">
                <Drawer
                        settings={settings}
                        isOpen={showCompetition}
                        onClose={() => null}
                        onChange={(sports) => setSetting(Object.assign({}, settings, {sports}))}
                />
                <Logo/>
                <ul className="flex items-center justify-around py-2 w-full">
                    <Separator/>
                    <li className="mx-4">
                        <Autocomplete
                                placeholder="search event"
                                searchAction={setSearchQuery}
                                suggestions={suggestions}
                        />
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <div className="relative flex flex-col ml-4">
                            <label htmlFor="dataFilter" className={"text-white"}>
                                DATE
                            </label>
                            <select
                                    id="dataFilter"
                                    name="dataFilter"
                                    className={"text-white bg-toolbar-bgMain"}
                                    onChange={({target}) =>
                                            setSetting(Object.assign({}, settings, {date: parseInt(target.value)}))
                                    }
                                    value={settings.date}
                            >
                                <option value={0}>All</option>
                                <option value={1}>Today</option>
                                <option value={2}>Today + 1</option>
                                <option value={3}>Today + 2</option>
                                <option value={4}>Today + 3</option>
                                <option value={5}>Today + 4</option>
                                <option value={6}>Today + 5</option>
                                <option value={7}>Today + 6</option>
                            </select>
                        </div>
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <FilterInput
                                id="from"
                                name="from"
                                label="FROM"
                                min={1}
                                max={50}
                                step={0.1}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (isNaN(parseInt(e.target.value))) return;
                                    setSetting(Object.assign({}, settings, {from: parseFloat(e.target.value)}));
                                }}
                                value={settings.from}
                        />
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <FilterInput
                                id="to"
                                name="to"
                                label="TO"
                                min={1}
                                max={100}
                                step={0.1}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (isNaN(parseInt(e.target.value))) return;
                                    setSetting(Object.assign({}, settings, {to: parseFloat(e.target.value)}));
                                }}
                                value={settings.to}
                        />
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <div className="relative flex flex-col">
                            <label htmlFor="playabilityFilter" className={"text-white"}>
                                PB:
                            </label>
                            <select
                                    id="playabilityFilter"
                                    name="playabilityFilter"
                                    className={"text-white bg-toolbar-bgMain"}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                            setSetting(Object.assign({}, settings, {playability: parseFloat(e.target.value)}))
                                    }
                                    value={settings.playability}
                            >
                                <option value={0}>All</option>
                                <option value={1}>1</option>
                                <option value={3}>3</option>
                            </select>
                        </div>
                    </li>
                    <Separator/>
                    <li className="flex flex-col items-center">
                        <Dropdown label="BOOKMAKERS" color="default">
                            {Object.values(bookmakers)
                                    //TODO remove optional of name
                                    .sort((a, b) => b.name?.toLowerCase() > a.name?.toLowerCase() ? -1 : 1)
                                    .map((bookmaker) => {
                                        if (bookmaker.isComparison) return null;
                                        const visible = settings.bookmakers.includes(bookmaker.id);
                                        return (
                                                <DropDownItem
                                                        key={bookmaker.name}
                                                        label={bookmaker.name}
                                                        selected={visible}
                                                        onClick={() => {
                                                            if (!visible) {
                                                                setSetting(
                                                                        Object.assign({}, settings, {
                                                                            bookmakers: [...settings.bookmakers, bookmaker.id],
                                                                        })
                                                                );
                                                            } else {
                                                                setSetting(
                                                                        Object.assign({}, settings, {
                                                                            bookmakers: settings.bookmakers.filter(
                                                                                    (item) => item !== bookmaker.id
                                                                            ),
                                                                        })
                                                                );
                                                            }
                                                        }}
                                                />
                                        );
                                    })}
                        </Dropdown>
                    </li>
                    <Separator/>
                    <li className="flex flex-col items-center">
                        <ComparisonsDropdown
                                settings={settings}
                                comparisons={comparisons}
                                loading={loading}
                                setSetting={(settings) => {
                                    setSetting(settings)
                                }}
                        />
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <div className="items-center flex">
                            <input
                                    id="tb-od-c"
                                    type="checkbox"
                                    className="2xl:h-5 2xl:w-5 h-3 w-3"
                                    checked={settings.onlyDrop}
                                    onChange={() => setSetting(Object.assign({}, settings, {onlyDrop: !settings.onlyDrop}))}
                            />
                            <label className="text-white ml-2" htmlFor="tb-od-c">
                                ONLY DROP
                            </label>
                        </div>
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <TemplateButton settings={settings} template={template} templates={templates}/>
                    </li>
                    <Separator/>

                    <li>
                        <button
                                className={`p-3 rounded-md ${
                                        loading
                                                ? "bg-toolbar-settingsLoading text-white "
                                                : pendingSettings
                                                        ? "bg-toolbar-settingsWarning text-black "
                                                        : "bg-toolbar-settings text-white "
                                }`}
                                onClick={() => {
                                    dispatch(SetSettingsAction(settings));
                                    dispatch(GetEventsAction({
                                        ...(settings.date && {date: settings.date}),
                                        ...(settings.bookmakers.length && {bookmakers: settings.bookmakers})
                                    }));
                                }}
                        >
                            <FontAwesomeIcon className={loading ? "animate-spin" : ""} icon={faRetweet}/>
                        </button>
                    </li>
                    <li className="mx-4">
                        <button
                                className={"peer h-10 bg-toolbar-bgMain text-white mx-3"}
                                onClick={() => setShowCompetition(!showCompetition)}
                        >
                            <FontAwesomeIcon icon={faTrophy}/>
                        </button>
                    </li>
                    <li className="mr-4">
                        <HeartBeatDropDown product="monitor"/>
                    </li>
                </ul>
                <div>{errorMessage}</div>
            </div>
    );
}
